import { useEffect, useState, useCallback, useRef } from "react";
import {
  fetchZonesPOReports,
  fetchZonePartiesPOReports,
  fetchSubdivisionPOReports,
  fetchOutstandingReports
} from "Service/ApiService";

/* ======================= DEBOUNCE ======================= */
const useDebounce = (value, delay = 600) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
};

const OutstandingReportsController = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const formatCustomerLabel = (p) =>
    `${p?.partyName?.trim() || ""} - ${p?.city?.trim() || ""} - ${p?.partyCode?.trim() || ""}`;

  /* ======================= DATA ======================= */
  const [outstandingData, setOutstandingData] = useState([]);
  const [summaryGraph, setSummaryGraph] = useState([]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [zoneOptions, setZoneOptions] = useState([]);
  const [subdivisionOptions, setSubdivisionOptions] = useState([]);
  const [customerNameOptions, setCustomerNameOptions] = useState([]);

  const [todaysDate, setTodaysDate] = useState("");
  const [divisionlist, setdivisionlist] = useState("");

  /* ======================= FILTERS ======================= */
  const [filters, setFilters] = useState({
    zone: [],
    subdivision: [],
    customerName: [],
    fromDate: "",
    toDate: ""
  });

  const debouncedFilters = useDebounce(filters, 600);

  /* ======================= LOADING ======================= */
  const [loadingOutstanding, setLoadingOutstanding] = useState(false);
  const [loadingZones, setLoadingZones] = useState(true);
  const [loadingSubdivisions, setLoadingSubdivisions] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const isFromApp =  localStorage.getItem('isFromApp') === 'true';
  const cloading =
    loadingOutstanding ||
    loadingZones ||
    loadingSubdivisions ||
    loadingCustomers;

  /* ======================= CONTROL FLAGS ======================= */
  const dropdownsCompletedRef = useRef(false);
  const [dropdownsCompleted, setDropdownsCompleted] = useState(false);

  const filtersRef = useRef(filters);
  const lastSignatureRef = useRef(null);
  const initialFiltersSetRef = useRef(false);
  const requestIdRef = useRef(0);

  /* ======================= KEEP FILTER REF UPDATED ======================= */
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  /* ======================= FILTER HANDLERS ======================= */
  const handleFilterChange = (field) => (value) => {
    setFilters((p) => ({ ...p, [field]: value || [] }));
    setPage(1);
  };

  const handleDateFromChange = (date) => {
    setFilters((p) => ({ ...p, fromDate: date }));
    setPage(1);
  };

  const handleDateToChange = (date) => {
    setFilters((p) => ({ ...p, toDate: date }));
    setPage(1);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage + 1);
  };

  /* ======================= LOAD ALL DROPDOWNS (ONCE) ======================= */
  useEffect(() => {
    let alive = true;

    const loadDropdowns = async () => {
      try {
        setLoadingZones(true);
        setLoadingSubdivisions(true);
        setLoadingCustomers(true);

        const [zoneRes, subRes, partyRes] = await Promise.all([
          fetchZonesPOReports(user.userCode, "outstanding"),
          fetchSubdivisionPOReports(user.userCode, "outstanding"),
          fetchZonePartiesPOReports(user.userCode, { type: "outstanding" })
        ]);

        if (!alive) return;

        setZoneOptions(
          zoneRes?.zones?.map((z, idx) => ({
            label: z.region?.trim() || "(NO REGION)",
            value: `${z.zoneCode}_${idx}`,
            zoneCode: z.zoneCode
          })) || []
        );

        setSubdivisionOptions(
          subRes?.divisions?.map((d) => ({
            label: `${d.divCode} - ${d.divName}`,
            value: d.divCode
          })) || []
        );

        setCustomerNameOptions(
          partyRes?.parties?.map((p) => ({
            label: formatCustomerLabel(p),
            value: p.partyCode
          })) || []
        );

        // ✅ MARK COMPLETED (DATA HO YA EMPTY)
        dropdownsCompletedRef.current = true;
        setDropdownsCompleted(true);
      } catch (err) {
        console.error("Dropdown load error:", err);
        dropdownsCompletedRef.current = true;
        setDropdownsCompleted(true);
      } finally {
        setLoadingZones(false);
        setLoadingSubdivisions(false);
        setLoadingCustomers(false);
      }
    };

    loadDropdowns();
    return () => (alive = false);
  }, [user.userCode]);

  /* ======================= AUTO SELECT ALL (ONCE) ======================= */
  useEffect(() => {
    if (!dropdownsCompleted || initialFiltersSetRef.current) return;

    initialFiltersSetRef.current = true;

    const nextFilters = {
      zone: zoneOptions,
      subdivision: subdivisionOptions,
      customerName: customerNameOptions,
      fromDate: "",
      toDate: ""
    };

    setFilters(nextFilters);
    filtersRef.current = nextFilters;
  }, [dropdownsCompleted, zoneOptions, subdivisionOptions, customerNameOptions]);

  /* ======================= CUSTOMER SEARCH ======================= */
  const handleCustomerSearch = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) return;

    try {
      setLoadingCustomers(true);
      const res = await fetchZonePartiesPOReports(user.userCode, {
        search: inputValue,
        type: "outstanding"
      });

      if (res?.status) {
        setCustomerNameOptions(
          res.parties.map((p) => ({
            label: formatCustomerLabel(p),
            value: p.partyCode
          }))
        );
      }
    } finally {
      setLoadingCustomers(false);
    }
  };
  

  /* ======================= REPORT API ======================= */
  const getOutstandingReports = useCallback(async () => {
    const f = filtersRef.current;
    const requestId = ++requestIdRef.current;

    try {
      setLoadingOutstanding(true);

      const res = await fetchOutstandingReports(
        user.userCode,
        page,
        f.zone.map((z) => z.zoneCode).join(","),
        f.subdivision.map((d) => d.value).join(","),
        f.customerName.map((c) => c.value).join(","),
        f.fromDate,
        f.toDate
      );

      // 🚫 Ignore outdated response
      if (requestId !== requestIdRef.current) return;

      if (res?.status) {
        setOutstandingData(res.ageing_table || []);
        setSummaryGraph(res.summary_graph || []);
        setTotalPages(res.totalPages || 0);
        setTotalRecords(res.totalRecords || 0);
        setTodaysDate(res.as_on_date || "");
        setdivisionlist(res.userDivisions || "");
      }
    } catch (err) {
      if (requestId === requestIdRef.current) {
        console.error("Outstanding API error:", err);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadingOutstanding(false);
      }
    }
  }, [user.userCode, page]);

  /* ======================= SIGNATURE ======================= */
  const buildSignature = (f, page) =>
    [
      f.zone.map((z) => z.zoneCode).join(","),
      f.subdivision.map((d) => d.value).join(","),
      f.customerName.map((c) => c.value).join(","),
      f.fromDate,
      f.toDate,
      page
    ].join("|");

  /* ======================= FINAL EFFECT ======================= */
  useEffect(() => {
    if (!dropdownsCompleted) return;

    const signature = buildSignature(debouncedFilters, page);

    if (lastSignatureRef.current === signature) return;

    lastSignatureRef.current = signature;
    getOutstandingReports();
  }, [dropdownsCompleted, debouncedFilters, page, getOutstandingReports]);
  
  

  /* ======================= EXPORT ======================= */
  return {
    outstandingData,
    summaryGraph,
    cloading,
    page,
    totalPages,
    totalRecords,
    zoneOptions,
    subdivisionOptions,
    customerNameOptions,
    filters,
    handleFilterChange,
    handleDateFromChange,
    handleDateToChange,
    handleChangePage,
    todaysDate,
    divisionlist,
    handleCustomerSearch,
    isFromApp
  };
};

export default OutstandingReportsController;
